import { useState } from "preact/hooks"
import useSWR from "swr"
import { Loader2 } from "lucide-react"
import { api_fetch_throws } from "../../../api/client"
import { UserState } from "../../../auth/user"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { isLauraChenelUser } from "../../../utils"
import { LiftTab } from "./lift_tab"
import { MatchTab } from "./match_tab"
import { MethodTab } from "./method_tab"
import { OverviewTab } from "./overview_tab"
import { ProfitTab } from "./profit_tab"
import { Figure } from "./trade_review_parts"
import { fmt_usd2, trade_review } from "./trade_review_model"
import { WatchTab } from "./watch_tab"

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "lift", label: "Promo lift" },
  { value: "profit", label: "Retailer profitability" },
  { value: "match", label: "Deductions to promotions" },
  { value: "watch", label: "Volume watchlist" },
  { value: "method", label: "How we measured" },
]

/** Laura Chenel's one-off review: a fixed snapshot the server only hands to this brand. */
export function TradeReviewPage() {
  const org_id = UserState.use((user) => user?.org_id)
  const allowed = isLauraChenelUser(org_id)
  const { data, error } = useSWR<trade_review>(allowed ? "/trade-review" : null, (path: string) => api_fetch_throws<trade_review>(path))
  const [tab, set_tab] = useState("overview")

  if (!allowed || error) return <p class="p-6 text-sm text-muted-foreground">This report isn't available for your account.</p>
  if (!data) return <div class="p-6"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>

  const s = data.summary
  return (
    <div class="p-6 space-y-6 max-w-[1500px]">
      <header class="space-y-1">
        <h1 class="text-2xl font-semibold">Trade Review</h1>
        <p class="max-w-3xl">
          Promos lift KeHE shipments about <Figure summary={s} field="lift" as="signed_pct" />, but each promo dollar buys only about <span class="font-medium tabular-nums">{fmt_usd2((s["inc_rev"] ?? 0) / (s["spend"] ?? 1))}</span> of extra sales. The decision is where to keep spending, and the answer differs by retailer.
        </p>
        <p class="text-sm text-muted-foreground">KeHE only. Shipments from January 1 to October 24, 2026, deductions through October 27. Laura Chenel and Marin French together.</p>
      </header>
      <Tabs value={tab} onValueChange={set_tab}>
        <TabsList className="h-auto flex-wrap">
          {TABS.map((item: { value: string; label: string }) => <TabsTrigger key={item.value} value={item.value}>{item.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
        <TabsContent value="lift"><LiftTab data={data} /></TabsContent>
        <TabsContent value="profit"><ProfitTab data={data} /></TabsContent>
        <TabsContent value="match"><MatchTab data={data} /></TabsContent>
        <TabsContent value="watch"><WatchTab data={data} /></TabsContent>
        <TabsContent value="method"><MethodTab summary={s} /></TabsContent>
      </Tabs>
    </div>
  )
}
